import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loading } from "../components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  fetchPurchaseRequestDetail,
  fetchStoresForPurchaseRequest,
  selectPurchaseRequestById,
  selectPurchaseRequestErrorById,
  selectPurchaseRequestStatusById,
  selectPurchaseRequestStores,
  selectPurchaseRequestStoresError,
  selectPurchaseRequestStoresStatus,
  selectUpdatePurchaseRequestErrorById,
  selectUpdatePurchaseRequestStatusById,
  updatePurchaseRequest,
} from "../features/purchaseRequests/purchaseRequestsSlice";
import "./StockTransferEditPage.css";

export default function PurchaseRequestEditPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const wrapRef = useRef(null);

  const request = useSelector(selectPurchaseRequestById(requestId));
  const loadStatus = useSelector(selectPurchaseRequestStatusById(requestId));
  const loadError = useSelector(selectPurchaseRequestErrorById(requestId));
  const saveStatus = useSelector(selectUpdatePurchaseRequestStatusById(requestId));
  const saveError = useSelector(selectUpdatePurchaseRequestErrorById(requestId));
  const stores = useSelector(selectPurchaseRequestStores);
  const storesStatus = useSelector(selectPurchaseRequestStoresStatus);
  const storesError = useSelector(selectPurchaseRequestStoresError);

  const [query, setQuery] = useState("");
  const [storeId, setStoreId] = useState(null);
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [formError, setFormError] = useState(null);

  const fromList = useMemo(() => {
    const from = location.state?.from;
    return typeof from === "string" && from.startsWith("/") ? from : null;
  }, [location.state]);

  const fetchRequest = useCallback(() => {
    dispatch(fetchPurchaseRequestDetail({ requestId }));
  }, [dispatch, requestId]);

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  useEffect(() => {
    if (!request) return;
    setStoreId(request.store ?? null);
    setComment(String(request.comment || ""));
    setQuery(request.store_name || (request.store != null ? `Store #${request.store}` : ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.id]);

  const debounceRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      dispatch(fetchStoresForPurchaseRequest({ search: query }));
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [dispatch, open, query]);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    function onKeyDown(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  const visibleStores = useMemo(() => (stores || []).slice(0, 30), [stores]);
  const canSave = useMemo(() => Boolean(storeId) && saveStatus !== "saving", [saveStatus, storeId]);

  const onSave = useCallback(
    async (e) => {
      e.preventDefault();
      setFormError(null);
      if (!storeId) {
        setFormError("Нужно выбрать склад (store)");
        return;
      }
      try {
        await dispatch(
          updatePurchaseRequest({
            requestId,
            store: Number(storeId),
            comment: String(comment || ""),
          })
        ).unwrap();
        navigate(`/warehouse/purchase-requests/${requestId}`, { replace: true, state: { from: fromList } });
      } catch {
        // error is in redux
      }
    },
    [comment, dispatch, fromList, navigate, requestId, storeId]
  );

  return (
    <div className="stock-transfer-edit-page">
      <div className="header">
        <div className="breadcrumbs">
          <Link to={fromList || "/warehouse/purchase-requests"} className="link">
            Запросы на закуп
          </Link>
          <span className="sep">/</span>
          <Link to={`/warehouse/purchase-requests/${requestId}`} state={{ from: fromList }} className="link">
            Заявка #{requestId}
          </Link>
          <span className="sep">/</span>
          <span className="muted">Редактирование</span>
        </div>
        <div className="title">Редактировать заявку #{requestId}</div>
        {request && (
          <div className="sub-title">
            Название: <b>{request.name || "—"}</b> · Статус: <b>{request.status || "—"}</b>
          </div>
        )}
      </div>

      {loadStatus === "loading" && <Loading text="Загрузка…" />}
      {(loadError || saveError || storesError || formError) && (
        <div className="error">{formError || loadError || saveError || storesError}</div>
      )}

      {request && (
        <form onSubmit={onSave} className="form">
          <label className="field">
            <div className="label">Склад (store)</div>
            <div className="combo-wrap" ref={wrapRef}>
              <input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setStoreId(null);
                  setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                placeholder="Начни вводить название/адрес…"
                className="input"
              />
              {open && (
                <div className="dropdown">
                  <div className="dropdown-hint">{storesStatus === "loading" ? "Загрузка…" : "Выбери склад"}</div>
                  {visibleStores.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className="dropdown-item"
                      onClick={() => {
                        setStoreId(s.id);
                        setQuery(`${s.name || `Store #${s.id}`}${s.address ? ` — ${s.address}` : ""}`);
                        setOpen(false);
                      }}
                    >
                      <div className="dropdown-title">{s.name || `Store #${s.id}`}</div>
                      <div className="dropdown-sub">
                        {s.address || "—"} · #{s.id}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </label>

          <label className="field">
            <div className="label">Комментарий (comment)</div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Комментарий…"
              className="textarea"
              rows={4}
            />
          </label>

          <div className="actions">
            <button type="submit" className="btn-primary" disabled={!canSave}>
              {saveStatus === "saving" ? "Сохраняю…" : "Сохранить"}
            </button>
            <Link to={`/warehouse/purchase-requests/${requestId}`} state={{ from: fromList }} className="btn-ghost">
              Отмена
            </Link>
            <button type="button" onClick={fetchRequest} className="btn">
              Перезагрузить
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
